import React, { useEffect, useMemo, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Grid,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import moment from "moment";
import MaterialReactTable from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { publicApi } from "../../api/http";
import { authAxios, authRequestHeaders, useAuth } from "../../contexts/AuthContext";
import { nhlTeamColors } from "../../components/PlayerCard/Constants";
import PlayerGraph from "../../components/PlayerGraph/PlayerGraph";
import PlayerDraftOddsTab from "../Player/PlayerDraftOddsTab";
import PlayerCompareTab from "../Player/PlayerCompareTab";
import PlayerScoutingReportTab from "../Player/PlayerScoutingReportTab";
import { goalieColumns, playerColumns } from "../Player/Constants";
import { DEFAULT_PLAYER_TAB, normalizePlayerTab } from "../Player/playerTabs";
import "../Player/Player.css";
import "./DraftHubPlayerPanel.css";

const HUB_TABS = [
    { value: "consensus", label: "Consensus" },
    { value: "draft-odds", label: "Draft Odds" },
    { value: "stats", label: "Stats" },
    { value: "film", label: "Film" },
    { value: "compare", label: "Compare" },
    { value: "scouting-reports", label: "Scouting Reports" },
];

const YOUTUBE_THUMB_QUALITIES = [
    { file: "maxresdefault.jpg" },
    { file: "hqdefault.jpg" },
    { file: "mqdefault.jpg" },
];

function getYouTubeThumbnailUrl(videoId, qualityIndex = 0) {
    if (!videoId) return "";
    const quality = YOUTUBE_THUMB_QUALITIES[Math.min(qualityIndex, YOUTUBE_THUMB_QUALITIES.length - 1)];
    return `https://i.ytimg.com/vi/${videoId}/${quality.file}`;
}

function FilmThumbnail({ videoId, alt, className, startQualityIndex = 2 }) {
    const [qualityIndex, setQualityIndex] = useState(startQualityIndex);

    useEffect(() => {
        setQualityIndex(startQualityIndex);
    }, [videoId, startQualityIndex]);

    if (!videoId) return null;

    return (
        <img
            src={getYouTubeThumbnailUrl(videoId, qualityIndex)}
            alt={alt}
            className={className}
            onError={() => setQualityIndex((current) => (
                current < YOUTUBE_THUMB_QUALITIES.length - 1 ? current + 1 : current
            ))}
            loading="lazy"
        />
    );
}

function getFilmKey(film) {
    return film?.id || film?.url || "";
}

function getYouTubeVideoId(url) {
    if (!url) return "";
    try {
        const parsedUrl = new URL(url);
        const videoId = parsedUrl.searchParams.get("v");
        if (videoId) return videoId;
        const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
        if (parsedUrl.hostname.includes("youtu.be")) return pathParts[0] || "";
        if (pathParts[0] === "embed" || pathParts[0] === "shorts") return pathParts[1] || "";
    } catch {
        const match = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([^&?/]+)/);
        return match?.[1] || "";
    }
    return "";
}

function getYouTubeEmbedUrl(url, { autoplay = false } = {}) {
    if (!url) return "";
    const videoId = getYouTubeVideoId(url);
    if (!videoId) return url;
    const base = `https://www.youtube.com/embed/${videoId}`;
    return autoplay ? `${base}?autoplay=1` : base;
}

function pickAndRoundToOverall(pick, round, year) {
    let teamCount = 30;
    if (year >= 2021) {
        teamCount = 32;
    } else if (year >= 2017) {
        teamCount = 31;
    }
    return pick + (round - 1) * teamCount;
}

const darkTheme = createTheme({
    palette: {
        mode: "dark",
        info: { main: "#7ea862", secondary: "#7ea862" },
        background: { default: "#14181d", paper: "#14181d" },
        text: { primary: "#f3f4f6", secondary: "#a1a8b3" },
    },
    shadows: ["none"],
});

function DraftHubPlayerPanel({ playerId, playerData }) {
    const { user, loading: authLoading, ensureAccessToken } = useAuth();
    const [activeTab, setActiveTab] = useState(DEFAULT_PLAYER_TAB);
    const [playerStats, setPlayerStats] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [lastConsensusData, setLastConsensusData] = useState(null);
    const [playerFilm, setPlayerFilm] = useState([]);
    const [activeFilmKey, setActiveFilmKey] = useState("");
    const [playingFilmKey, setPlayingFilmKey] = useState("");
    const [scoutingReportCount, setScoutingReportCount] = useState(0);
    const [watchlistLoading, setWatchlistLoading] = useState(false);
    const [isOnWatchlist, setIsOnWatchlist] = useState(false);

    const hasFilm = playerFilm.length > 0;

    const filteredPlayerStats = useMemo(() => {
        if (!playerStats || !playerData?.draft_year) return playerStats;
        const draftYear = playerData.draft_year;
        const targetSeason = `${draftYear - 1}-${String(draftYear).slice(-2)}`;
        return playerStats.filter((stat) => stat.year === targetSeason);
    }, [playerStats, playerData]);

    const activeFilm = useMemo(
        () => playerFilm.find((film) => getFilmKey(film) === activeFilmKey) || playerFilm[0] || null,
        [activeFilmKey, playerFilm],
    );

    useEffect(() => {
        setActiveTab(DEFAULT_PLAYER_TAB);
    }, [playerId]);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !playerId) {
            setIsOnWatchlist(false);
            return;
        }

        let cancelled = false;
        (async () => {
            setWatchlistLoading(true);
            try {
                const token = await ensureAccessToken();
                if (cancelled) return;
                if (!token) {
                    setIsOnWatchlist(false);
                    return;
                }
                const res = await authAxios.get("/watchlist", { headers: authRequestHeaders(token) });
                if (cancelled) return;
                const items = Array.isArray(res.data) ? res.data : [];
                setIsOnWatchlist(items.some((p) => p?.player_id === playerId));
            } catch (err) {
                if (!cancelled) {
                    console.warn("[draft-hub watchlist] load failed", err.response?.status, err.response?.data);
                    setIsOnWatchlist(false);
                }
            } finally {
                if (!cancelled) setWatchlistLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authLoading, user, playerId, ensureAccessToken]);

    const toggleWatchlist = async () => {
        if (watchlistLoading || !user || !playerId) return;
        setWatchlistLoading(true);
        try {
            const token = await ensureAccessToken();
            if (!token) {
                setIsOnWatchlist(false);
                return;
            }
            if (isOnWatchlist) {
                await authAxios.delete(`/watchlist/${playerId}`, { headers: authRequestHeaders(token) });
                setIsOnWatchlist(false);
            } else {
                await authAxios.post(`/watchlist/${playerId}`, null, { headers: authRequestHeaders(token) });
                setIsOnWatchlist(true);
            }
        } catch (err) {
            console.warn("[draft-hub watchlist] toggle failed", err.response?.status, err.response?.data);
        } finally {
            setWatchlistLoading(false);
        }
    };

    useEffect(() => {
        if (!playerId) return;
        publicApi.get(`/player_stats/${playerId}`)
            .then((res) => setPlayerStats(res.data))
            .catch(() => setPlayerStats([]));
    }, [playerId]);

    useEffect(() => {
        if (!playerId) return;
        publicApi.get(`/film/${playerId}`)
            .then((res) => setPlayerFilm(Array.isArray(res.data) ? res.data : []))
            .catch(() => setPlayerFilm([]));
    }, [playerId]);

    useEffect(() => {
        if (!playerId) return;
        publicApi.get(`/consensus_rankings/${playerId}`)
            .then((res) => {
                const data = res.data || [];
                setLastConsensusData(data[data.length - 1] || null);
                setGraphData(data.map((item) => ({
                    effective_date: moment(item.effective_date).valueOf(),
                    score: Math.round(item.score * 1e2) / 1e2,
                    lastRank: item.has_data_point ? item.last_rank : null,
                    consensusRanking: item.rank,
                    lastRankType: item.last_rank_type,
                })));
            })
            .catch(() => {
                setLastConsensusData(null);
                setGraphData(null);
            });
    }, [playerId]);

    useEffect(() => {
        if (!hasFilm) {
            setActiveFilmKey("");
            return;
        }
        if (!playerFilm.some((film) => getFilmKey(film) === activeFilmKey)) {
            setActiveFilmKey(getFilmKey(playerFilm[0]));
        }
    }, [activeFilmKey, hasFilm, playerFilm]);

    useEffect(() => {
        setPlayingFilmKey("");
    }, [activeFilmKey]);

    useEffect(() => {
        const normalized = normalizePlayerTab(activeTab, { hasFilm });
        if (normalized !== activeTab) setActiveTab(normalized);
    }, [activeTab, hasFilm]);

    if (!playerId || !playerData) {
        return (
            <Box className="draft-hub-player-panel draft-hub-player-panel--empty">
                <Typography variant="body2">Select a player from the list to view details.</Typography>
            </Box>
        );
    }

    const visibleTabs = HUB_TABS.filter((tab) => tab.value !== "film" || hasFilm);
    const scoutingLabel = scoutingReportCount > 0
        ? `Scouting Reports [${scoutingReportCount}]`
        : "Scouting Reports";

    const draftResults = playerData.draft_results;
    const draftTeam = draftResults?.team;
    const draftTeamColor = draftTeam?.team_id
        ? nhlTeamColors[draftTeam.team_id]
        : undefined;
    const draftOverall = draftResults
        ? pickAndRoundToOverall(
            draftResults.pick,
            draftResults.round,
            draftResults.draft_year,
        )
        : null;
    const flagUrl = playerData.country?.flag_url || playerData.flag_url;
    const detailItems = [];
    if (playerData.dob) {
        detailItems.push(`DOB: ${format(new Date(playerData.dob), "MMM d, yyyy")}`);
    }
    if (playerData.height) {
        detailItems.push(playerData.height);
    }
    if (playerData.weight) {
        detailItems.push(`${playerData.weight} lbs`);
    }
    if (playerData.handedness) {
        detailItems.push(`Shoots ${playerData.handedness === "L" ? "Left" : "Right"}`);
    }

    return (
        <Box className="draft-hub-player-panel">
            <Box className="draft-hub-player-header">
                <Avatar
                    src={playerData.player_image_url || undefined}
                    alt={`${playerData.first_name} ${playerData.last_name}`}
                    className="draft-hub-player-header-avatar"
                    variant="rounded"
                >
                    {playerData.first_name?.[0]}
                </Avatar>
                <Box className="draft-hub-player-header-main">
                    <Box className="draft-hub-player-header-row">
                        <Box className="draft-hub-player-header-identity">
                            <span className="draft-hub-player-first-name">
                                {playerData.first_name}
                            </span>
                            <Typography component="h2" className="draft-hub-player-last-name">
                                {playerData.last_name}
                            </Typography>
                        </Box>
                        <Box className="draft-hub-player-header-actions">
                            {user && (
                                <Button
                                    onClick={toggleWatchlist}
                                    disabled={watchlistLoading}
                                    size="small"
                                    variant={isOnWatchlist ? "outlined" : "contained"}
                                    disableElevation
                                    startIcon={
                                        watchlistLoading ? (
                                            <CircularProgress size={14} />
                                        ) : isOnWatchlist ? (
                                            <StarRoundedIcon sx={{ fontSize: "0.9rem" }} />
                                        ) : (
                                            <StarBorderRoundedIcon sx={{ fontSize: "0.9rem" }} />
                                        )
                                    }
                                    sx={{
                                        textTransform: "none",
                                        fontSize: "0.75rem",
                                        flexShrink: 0,
                                        minHeight: 30,
                                        ...(isOnWatchlist
                                            ? {
                                                color: "var(--brand-strong)",
                                                borderColor: "var(--brand-strong)",
                                                "&:hover": {
                                                    borderColor: "var(--brand-strong)",
                                                    backgroundColor: "var(--brand-soft)",
                                                },
                                            }
                                            : {
                                                color: "#0b0d10",
                                                background: "linear-gradient(145deg, var(--brand-strong), var(--brand))",
                                                "&:hover": {
                                                    background: "linear-gradient(145deg, var(--brand), var(--brand-strong))",
                                                },
                                            }),
                                    }}
                                >
                                    {isOnWatchlist ? "On watchlist" : "Add to watchlist"}
                                </Button>
                            )}
                            <Button
                                component={Link}
                                to={`/player/${playerId}`}
                                size="small"
                                variant="outlined"
                                endIcon={<OpenInNewRoundedIcon sx={{ fontSize: "0.9rem" }} />}
                                sx={{
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    flexShrink: 0,
                                    minHeight: 30,
                                    color: "var(--text-primary)",
                                    borderColor: "var(--border-strong)",
                                }}
                            >
                                View player page
                            </Button>
                        </Box>
                    </Box>
                    <Box className="draft-hub-player-header-chips">
                        <span className="draft-hub-player-chip draft-hub-player-chip--rank">
                            #{playerData.consensus_ranking ?? "—"}
                        </span>
                        {playerData.position && (
                            <span className="draft-hub-player-chip">{playerData.position}</span>
                        )}
                        {flagUrl && (
                            <img
                                src={flagUrl}
                                alt=""
                                className="draft-hub-player-flag"
                            />
                        )}
                        {draftTeam && (
                            <span
                                className="draft-hub-player-chip draft-hub-player-chip--draft"
                                style={draftTeamColor ? { backgroundColor: draftTeamColor } : undefined}
                            >
                                {draftTeam.team_logo_url && (
                                    <img
                                        src={draftTeam.team_logo_url}
                                        alt=""
                                        className="draft-hub-player-draft-chip-logo"
                                    />
                                )}
                                {draftResults.draft_year} · {draftOverall} Overall · {draftTeam.team_name}
                            </span>
                        )}
                    </Box>
                    {detailItems.length > 0 && (
                        <p className="draft-hub-player-header-details">
                            {detailItems.join(" · ")}
                        </p>
                    )}
                </Box>
            </Box>

            <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                className="player-tabs draft-hub-player-tabs"
                variant="scrollable"
                scrollButtons="auto"
            >
                {visibleTabs.map((tab) => (
                    <Tab
                        key={tab.value}
                        value={tab.value}
                        label={tab.value === "scouting-reports" ? scoutingLabel : tab.label}
                    />
                ))}
            </Tabs>

            <Box className="draft-hub-player-tab-scroll">
                <Box className="player-tab-panel" hidden={activeTab !== "consensus"}>
                    {lastConsensusData ? (
                        <>
                            {graphData && <PlayerGraph graphData={graphData} />}
                            <Grid container spacing={2} className="rank-stat-grid">
                                <Grid item xs={6} sm={4} md={12 / 5}>
                                    <div className="rank-stat">
                                        <Typography variant="h4" component="div" className="rank-stat-title">
                                            Number of Rankings:
                                        </Typography>
                                        <Typography variant="h4" component="div" className="rank-stat-data">
                                            {lastConsensusData.count}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={12 / 5}>
                                    <div className="rank-stat">
                                        <Typography variant="h4" component="div" className="rank-stat-title">
                                            Highest Ranking:
                                        </Typography>
                                        <Typography variant="h4" component="div" className="rank-stat-data">
                                            {lastConsensusData.best_rank}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={12 / 5}>
                                    <div className="rank-stat">
                                        <Typography variant="h4" component="div" className="rank-stat-title">
                                            Lowest Ranking:
                                        </Typography>
                                        <Typography variant="h4" component="div" className="rank-stat-data">
                                            {lastConsensusData.worst_rank}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={12 / 5}>
                                    <div className="rank-stat">
                                        <Typography variant="h4" component="div" className="rank-stat-title">
                                            Last Ranking:
                                        </Typography>
                                        <Typography variant="h4" component="div" className="rank-stat-data">
                                            {lastConsensusData.last_rank}
                                        </Typography>
                                    </div>
                                </Grid>
                                <Grid item xs={6} sm={4} md={12 / 5}>
                                    <div className="rank-stat">
                                        <Typography variant="h4" component="div" className="rank-stat-title">
                                            Standard Deviation:
                                        </Typography>
                                        <Typography variant="h4" component="div" className="rank-stat-data">
                                            {Math.round(lastConsensusData.std_dev * 1e2) / 1e2}
                                        </Typography>
                                    </div>
                                </Grid>
                            </Grid>
                        </>
                    ) : (
                        <Typography variant="body1" component="div" className="empty-tab-message">
                            No consensus ranking history available for this player.
                        </Typography>
                    )}
                </Box>

                <Box className="player-tab-panel" hidden={activeTab !== "stats"}>
                    <Typography variant="h4" component="div" className="tab-section-title">
                        Draft Year Stats
                    </Typography>
                    {filteredPlayerStats && (
                        <>
                            <ThemeProvider theme={darkTheme}>
                                <MaterialReactTable
                                    columns={(playerData.position === "G") ? goalieColumns : playerColumns}
                                    data={filteredPlayerStats}
                                    initialState={{ density: "compact", pagination: { pageSize: 10 } }}
                                />
                            </ThemeProvider>
                            {filteredPlayerStats.length === 0 && (
                                <Typography variant="body1" component="div" className="no-stats-message">
                                    No stats recorded for the player&apos;s draft year.
                                </Typography>
                            )}
                        </>
                    )}
                </Box>

                {hasFilm && (
                    <Box className="player-tab-panel film-tab-panel" hidden={activeTab !== "film"}>
                        <Grid container spacing={2} className="film-browser">
                            <Grid item xs={12} md={3} className="film-list-column">
                                <div className="film-list">
                                    {playerFilm.map((film) => {
                                        const filmKey = getFilmKey(film);
                                        const videoId = getYouTubeVideoId(film.url);
                                        const isActiveFilm = getFilmKey(activeFilm) === filmKey;
                                        return (
                                            <button
                                                type="button"
                                                key={filmKey}
                                                className={`film-list-item${isActiveFilm ? " film-list-item--active" : ""}`}
                                                onClick={() => setActiveFilmKey(filmKey)}
                                            >
                                                {videoId && (
                                                    <FilmThumbnail
                                                        videoId={videoId}
                                                        alt=""
                                                        className="film-list-thumbnail"
                                                        startQualityIndex={1}
                                                    />
                                                )}
                                                <span className="film-list-title">
                                                    {film.title || "Player Film"}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </Grid>
                            <Grid item xs={12} md={9} className="film-active-column">
                                {activeFilm && (
                                    <div className="film-active-card">
                                        <div className="film-active-frame">
                                            {playingFilmKey === getFilmKey(activeFilm) ? (
                                                <iframe
                                                    width="100%"
                                                    height="100%"
                                                    src={getYouTubeEmbedUrl(activeFilm.url, { autoplay: true })}
                                                    title={activeFilm.title || "Player film"}
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                    allowFullScreen
                                                />
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="film-active-poster"
                                                    onClick={() => setPlayingFilmKey(getFilmKey(activeFilm))}
                                                    aria-label={`Play ${activeFilm.title || "player film"}`}
                                                >
                                                    <FilmThumbnail
                                                        videoId={getYouTubeVideoId(activeFilm.url)}
                                                        alt=""
                                                        className="film-active-poster-image"
                                                        startQualityIndex={0}
                                                    />
                                                    <span className="film-active-play-icon" aria-hidden="true" />
                                                </button>
                                            )}
                                        </div>
                                        <Typography variant="body1" component="div" className="film-title">
                                            {activeFilm.title || "Player Film"}
                                        </Typography>
                                    </div>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                )}

                <PlayerDraftOddsTab
                    playerId={playerId}
                    draftYear={playerData?.draft_year}
                    draftResults={playerData?.draft_results}
                    isActive={activeTab === "draft-odds"}
                />

                <PlayerCompareTab
                    playerId={playerId}
                    playerData={playerData}
                    isActive={activeTab === "compare"}
                />

                <PlayerScoutingReportTab
                    playerId={playerId}
                    playerData={playerData}
                    isActive={activeTab === "scouting-reports"}
                    onReportCountChange={setScoutingReportCount}
                />
            </Box>
        </Box>
    );
}

export default DraftHubPlayerPanel;
